import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCents, formatCount, formatPercent, bpsToPercent } from '@affily/shared/money';
import { Seo } from '../../components/Seo.jsx';
import {
  DEFAULT_TERMS,
  TermsInputs,
  useProjection,
  ResultRow,
  RetentionField,
  FeeNote,
} from '../../components/Calculator.jsx';
import { Button, Field, Input, SectionTitle, Alert } from '../../components/ui.jsx';

/**
 * Brand-side program calculator.
 *
 * Answers a different question from the creator's version: not "what will I
 * earn" but "what am I committing to". For a recurring program those are the
 * same arithmetic and very different psychology — the cost of one month's
 * acquisition is a payment schedule stretching a year out, and most brands
 * publish a recurring rate without ever having drawn that schedule.
 */
export default function ProgramCalculator() {
  const [terms, setTerms] = useState({ ...DEFAULT_TERMS, recurringMode: 'fixed_period' });
  const [inputs, setInputs] = useState({
    clicks: '5000',
    conversionRatePct: '2',
    orderAmount: '50',
    cycleRetentionPct: '90',
    grossMarginPct: '80',
  });

  const setTerm = (key) => (e) => setTerms((f) => ({ ...f, [key]: e.target.value }));
  const setInput = (key) => (e) => setInputs((f) => ({ ...f, [key]: e.target.value }));

  const assumptions = {
    clicks: Number(inputs.clicks) || 0,
    conversionRatePct: Number(inputs.conversionRatePct) || 0,
    orderAmountCents: Math.round(Number(inputs.orderAmount || 0) * 100),
    cycleRetentionPct: Number(inputs.cycleRetentionPct) || 100,
  };

  const { projection, liability, terms: realTerms } = useProjection(terms, assumptions);
  const recurring = realTerms?.recurringMode === 'fixed_period';

  // Margin matters here in a way it doesn't on the creator side: a 20%
  // commission on an 80%-margin product is a quarter of the profit, not a fifth
  // of it, and that is the number that decides whether a program is viable.
  const marginPct = Number(inputs.grossMarginPct) || 0;
  const grossProfitCents = projection
    ? Math.round((projection.revenueCents * marginPct) / 100)
    : 0;
  const profitAfterCommissionCents = grossProfitCents - (projection?.costCents ?? 0);
  const commissionShareOfProfit =
    grossProfitCents > 0 ? ((projection?.costCents ?? 0) / grossProfitCents) * 100 : null;

  return (
    <>
      <Seo
        title="Affiliate program cost calculator for brands"
        description="Work out what an affiliate or creator program will cost you — cost per acquisition, commission as a share of margin, and the forward liability of a recurring rate. Free, no signup."
      />

      <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
        <p className="text-2xs font-semibold uppercase tracking-widest text-accent">Free tool</p>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight">
          Affiliate program cost calculator
        </h1>
        <p className="mt-3 max-w-xl text-ink-muted">
          What a commission rate actually costs you before you publish it — including the part a
          recurring rate commits you to for the next year.
        </p>

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
            <section>
              <SectionTitle hint="What you're thinking of offering">Your terms</SectionTitle>
              <TermsInputs form={terms} set={setTerm} />
            </section>

            <section>
              <SectionTitle hint="What you expect creators to send">Volume</SectionTitle>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Clicks per month">
                  <Input
                    type="number"
                    min="0"
                    value={inputs.clicks}
                    onChange={setInput('clicks')}
                  />
                </Field>
                <Field label="Conversion rate (%)">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={inputs.conversionRatePct}
                    onChange={setInput('conversionRatePct')}
                  />
                </Field>
                <Field label="Average order (USD)">
                  <Input
                    type="number"
                    min="0"
                    value={inputs.orderAmount}
                    onChange={setInput('orderAmount')}
                  />
                </Field>
                <Field
                  label="Gross margin (%)"
                  hint="Commission comes out of margin, not revenue — this is what decides viability."
                >
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={inputs.grossMarginPct}
                    onChange={setInput('grossMarginPct')}
                  />
                </Field>
                {recurring && (
                  <RetentionField
                    value={inputs.cycleRetentionPct}
                    onChange={setInput('cycleRetentionPct')}
                    audience="brand"
                  />
                )}
              </div>
            </section>

            {recurring && liability?.isRecurring && (
              <section>
                <SectionTitle hint="From one month of acquisition, assuming you stop acquiring">
                  Your forward commitment
                </SectionTitle>
                <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Cycle</th>
                        <th className="num">Customers still billing</th>
                        <th className="num">Commission owed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liability.cycles.map((c) => (
                        <tr key={c.cycle}>
                          <td>{c.cycle}</td>
                          <td className="num">{formatCount(Math.round(c.customers))}</td>
                          <td className="num">{formatCents(c.costCents)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="font-semibold">Total</td>
                        <td />
                        <td className="num font-semibold">{formatCents(liability.totalCents)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs text-ink-faint">
                  This is what one month of acquisition costs you over the whole window, even if you
                  pause the program tomorrow. Each additional month of acquisition stacks another
                  schedule on top.
                </p>
              </section>
            )}
          </form>

          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="rounded-lg border border-rule bg-paper-raised p-5">
              <h2 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                What it costs you
              </h2>

              {projection ? (
                <>
                  <p className="mt-2 text-4xl font-semibold tabular-nums text-accent">
                    {formatCents(projection.costCents)}
                  </p>
                  <p className="text-xs text-ink-faint">
                    {recurring ? 'per month, once the window is full' : 'per month'}
                  </p>

                  <div className="mt-4 border-t border-rule pt-2">
                    <ResultRow
                      label="New customers"
                      value={formatCount(Math.floor(projection.conversions))}
                    />
                    <ResultRow
                      label="Revenue generated"
                      value={formatCents(projection.revenueCents)}
                    />
                    <ResultRow
                      label="Cost per acquisition"
                      value={
                        projection.costPerAcquisitionCents === null
                          ? null
                          : formatCents(projection.costPerAcquisitionCents)
                      }
                      empty="No conversions at these numbers"
                    />
                    <ResultRow
                      label="Commission on revenue"
                      value={
                        projection.costOfSaleBps === null
                          ? null
                          : formatPercent(bpsToPercent(projection.costOfSaleBps), { digits: 1 })
                      }
                      empty="No revenue at these numbers"
                    />
                    <ResultRow
                      label="Return on commission"
                      value={
                        projection.returnOnSpend === null
                          ? null
                          : `${projection.returnOnSpend.toFixed(1)}×`
                      }
                      empty="No commission at these numbers"
                    />
                  </div>

                  <div className="mt-4 border-t border-rule pt-3">
                    <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                      Against your margin
                    </p>
                    <ResultRow
                      label="Gross profit"
                      value={formatCents(grossProfitCents)}
                      hint={`at ${marginPct}% margin`}
                    />
                    <ResultRow
                      label="Commission takes"
                      value={
                        commissionShareOfProfit === null ? null : (
                          formatPercent(commissionShareOfProfit, { digits: 0 })
                        )
                      }
                      empty="No profit at these numbers"
                      hint="of your gross profit"
                    />
                    <ResultRow
                      label="Profit after commission"
                      value={formatCents(profitAfterCommissionCents)}
                      emphasis
                    />
                  </div>

                  {profitAfterCommissionCents < 0 && (
                    <Alert tone="negative" className="mt-4">
                      At these numbers the program costs more than the margin it generates. Worth
                      knowing now rather than after you’ve published the rate.
                    </Alert>
                  )}
                </>
              ) : (
                <p className="mt-2 text-sm text-ink-muted">Fill in the terms to see a projection.</p>
              )}

              <FeeNote audience="brand" />
            </div>

            <div className="mt-4 rounded-lg border border-rule bg-paper-sunken p-4">
              <p className="text-sm font-medium">See what others actually pay</p>
              <p className="mt-1 text-xs text-ink-muted">
                Every program on Affily publishes its full terms, so you can price against the real
                market rather than guessing at it.
              </p>
              <Button to="/programs" size="sm" className="mt-3">
                Browse programs
              </Button>
            </div>
          </aside>
        </div>

        <section className="mt-16 max-w-2xl">
          <h2 className="text-xl font-semibold tracking-tight">
            A recurring rate is a schedule, not a price
          </h2>
          <p className="mt-3 text-ink-muted">
            One-time commission is an expense: you pay it, it’s done. Recurring commission is a
            liability — every customer acquired this month keeps costing you for as long as they
            keep paying, and the bill from month one is still arriving in month twelve.
          </p>
          <p className="mt-3 text-ink-muted">
            That is usually the right trade, because a customer who stays a year is worth far more
            than one who buys once, and paying creators accordingly is what gets you those
            customers. But it is worth having drawn the schedule before you publish the rate, not
            after.
          </p>
          <p className="mt-3 text-ink-muted">
            Affily caps recurring at 60 billing cycles and has no lifetime option, for the same
            reason: a perpetual claim on your revenue is closer to equity than to a commission, and
            it can’t be reasoned about at the moment you agree to it.
          </p>
          <Link
            to="/tools/affiliate-earnings-calculator"
            className="mt-4 inline-block text-sm font-medium text-accent hover:underline"
          >
            Want the creator’s side of the same maths? →
          </Link>
        </section>
      </div>
    </>
  );
}
